import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTech } from '../lib/api';
import { MapPin, Wrench, Search, ChevronDown, Star, X, SlidersHorizontal } from 'lucide-react';
import { ListingCard, CardRow, CardTitle, CardPrice, CardDivider, CardTags, CardMetric } from '../components/ListingCard';
import { cn } from '../lib/utils';
import DirectoryLayout, { useBreakpoint } from '../components/layouts/DirectoryLayout';
import TechDetailPage from './TechDetailPage';
import { EVENTS } from '../constants/taxonomy';
import { ROLES, RATE_BUCKETS, SORT_OPTIONS } from '../constants/profileContract';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

const RATE_OPTIONS = RATE_BUCKETS.tech;

export default function TechPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const selectedId = params.id || null;

  const [techCrew, setTechCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [rateFilter, setRateFilter] = useState(null);
  const [hasEquipmentOnly, setHasEquipmentOnly] = useState(false);
  const [highRatedOnly, setHighRatedOnly] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTech(); }, [sortBy]);

  const loadTech = async () => {
    setLoading(true);
    try {
      const p = {};
      if (sortBy) p.sort = sortBy;
      const response = await getTech(p);
      setTechCrew(response.data);
    } catch (error) {
      console.error('Failed to load tech crew');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handler = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortDropdownOpen]);

  const toggleRole = (role) => setSelectedRoles((prev) => prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]);
  const toggleEventType = (t) => setSelectedEventTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedRoles([]);
    setSelectedEventTypes([]);
    setRateFilter(null);
    setHasEquipmentOnly(false);
    setHighRatedOnly(false);
  };

  const hasActiveFilters = searchQuery || selectedRoles.length > 0 || selectedEventTypes.length > 0 ||
    rateFilter || hasEquipmentOnly || highRatedOnly;

  const activeFilterCount = [
    selectedRoles.length > 0 ? 1 : 0,
    selectedEventTypes.length > 0 ? 1 : 0,
    rateFilter ? 1 : 0,
    hasEquipmentOnly ? 1 : 0,
    highRatedOnly ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const filteredTech = useMemo(() => techCrew.filter((t) => {
    if (!t.stage8GigCount || t.stage8GigCount === 0) return false;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hit = [t.displayName, t.city, t.primaryRole].some((v) => v?.toLowerCase().includes(q));
      if (!hit) return false;
    }

    if (selectedRoles.length > 0) {
      const techRoles = [t.primaryRole, ...(t.specialties || [])].filter(Boolean);
      const hasMatch = selectedRoles.some((role) => techRoles.some((tr) => tr.toLowerCase().includes(role.toLowerCase())));
      if (!hasMatch) return false;
    }

    if (selectedEventTypes.length > 0) {
      const hasMatch = selectedEventTypes.some((type) => (t.eventTypes || []).some((et) => et.toLowerCase().includes(type.toLowerCase())));
      if (!hasMatch) return false;
    }

    if (rateFilter) {
      const rate = t.baseRate || 0;
      if (rateFilter.min !== undefined && rate < rateFilter.min) return false;
      if (rateFilter.max !== undefined && rate >= rateFilter.max) return false;
    }

    if (hasEquipmentOnly && !t.hasOwnEquipment) return false;
    if (highRatedOnly && (!t.averageRating || t.averageRating < 4.5)) return false;

    return true;
  }), [techCrew, searchQuery, selectedRoles, selectedEventTypes, rateFilter, hasEquipmentOnly, highRatedOnly]);

  const handleCardClick = (techId, e) => {
    if (isMobile) return;
    e.preventDefault();
    navigate(`/tech/${techId}`);
  };

  const sortDropdown = (
    <div className="relative" ref={sortRef}>
      <button onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
        className="flex items-center gap-1.5 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
        style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-muted)', border: '1px solid var(--s8-border-card)', borderRadius: 'var(--s8-radius-md)' }}
        data-testid="sort-dropdown-btn"
      >
        {SORT_OPTIONS.find((o) => o.key === sortBy)?.label || 'Best match'}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {sortDropdownOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 py-1 min-w-[160px]" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', borderRadius: 'var(--s8-radius-md)' }}>
          {SORT_OPTIONS.map((opt) => (
            <button key={opt.key || 'default'} onClick={() => { setSortBy(opt.key); setSortDropdownOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'var(--s8-font-body)', color: sortBy === opt.key ? 'var(--s8-amber)' : 'var(--s8-text-body)', background: sortBy === opt.key ? 'rgba(200,146,62,0.06)' : 'transparent' }}
              data-testid={`sort-${opt.key || 'default'}`}
            >{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );

  const filterPanel = (
    <>
      <div className="flex items-center gap-3 mb-3 browse-input w-full">
        <Search className="h-4 w-4 shrink-0 text-[var(--s8-text-muted)]" />
        <input type="text" placeholder="Search name, city, role..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-transparent border-none outline-none w-full text-[var(--s8-text-body)] placeholder:text-[var(--s8-text-muted)]"
          style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', padding: 0 }} data-testid="search-input" />
      </div>

      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Role</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {ROLES.map((role) => (
            <button key={role} onClick={() => toggleRole(role)}
              className={cn('browse-chip', selectedRoles.includes(role) && 'browse-chip-active')}
              data-testid={`filter-role-${role.toLowerCase()}`}
            >{role}</button>
          ))}
        </div>
      </div>

      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Events</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {EVENTS.map((type) => (
            <button key={type} onClick={() => toggleEventType(type)}
              className={cn('browse-chip', selectedEventTypes.includes(type) && 'browse-chip-active')}
              data-testid={`filter-event-${type.toLowerCase()}`}
            >{type}</button>
          ))}
        </div>
      </div>

      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Rate</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {RATE_OPTIONS.map((opt) => (
            <button key={opt.label} onClick={() => setRateFilter(rateFilter?.label === opt.label ? null : opt)}
              className={cn('browse-chip', rateFilter?.label === opt.label && 'browse-chip-active')}
              data-testid={`filter-rate-${opt.label}`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      <div className="sidebar-section">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setHighRatedOnly(!highRatedOnly)}
            className={cn('browse-chip flex items-center gap-1.5', highRatedOnly && 'browse-chip-active')}
            data-testid="rating-filter"
          >
            <Star className={cn('h-3 w-3', highRatedOnly && 'fill-[var(--s8-accent)]')} />4.5+ rated
          </button>
          <button onClick={() => setHasEquipmentOnly(!hasEquipmentOnly)}
            className={cn('browse-chip flex items-center gap-1.5', hasEquipmentOnly && 'browse-chip-active')}
            data-testid="equipment-toggle"
          >
            <Wrench className={cn('h-3 w-3', hasEquipmentOnly && 'text-[var(--s8-accent)]')} />Own gear
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters}
              className="browse-chip flex items-center gap-1.5 hover:text-[var(--red)] hover:border-[var(--red)]"
              style={{ color: 'var(--s8-text-muted)', borderColor: 'var(--s8-border-card)' }}
              data-testid="clear-filters-btn"
            >
              <X className="h-3 w-3" />Clear all
            </button>
          )}
        </div>
      </div>
    </>
  );

  const cardList = loading ? (
    <PageSkeleton label="Loading tech crew" className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <BoneCard key={i} className="px-4 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <Bone w="58%" h={22} />
            <Bone w={72} h={20} tone="amber" />
          </div>
          <Bone w="52%" h={14} className="mt-3" />
          <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--s8-b1-solid)' }}>
            <div className="flex gap-2"><Bone w={56} h={22} /><Bone w={72} h={22} /></div>
            <Bone w={88} h={14} />
          </div>
        </BoneCard>
      ))}
    </PageSkeleton>
  ) : filteredTech.length === 0 ? (
    <div className="text-center py-12">
      <Wrench className="h-5 w-5 text-[var(--s8-text-dim)] mx-auto mb-3" />
      <p className="text-[var(--s8-text-muted)] text-sm">No tech crew match your filters</p>
      {hasActiveFilters && <button onClick={clearFilters} className="mt-3 text-[var(--s8-accent)] hover:text-[var(--s8-text-main)] text-sm">Clear All Filters</button>}
    </div>
  ) : (
    filteredTech.map((tech, index) => {
      const cardClass = !isMobile && selectedId === tech.id ? 's8-card--selected' : '';
      return (
        <ListingCard key={tech.id} to={`/tech/${tech.id}`} index={index} cardClass={cardClass} testId={`tech-card-${tech.id}`}
          onClick={(e) => handleCardClick(tech.id, e)}
        >
          <CardRow><CardTitle>{tech.displayName}</CardTitle><CardPrice amount={tech.baseRate ? `€${tech.baseRate}` : null} sub={tech.baseRate ? (tech.rateType === 'HOURLY' ? 'per hr' : 'per gig') : null} /></CardRow>
          <div className="s8-venue"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{tech.city}{tech.travelRadiusKm ? ` · ${tech.travelRadiusKm}km radius` : ''}</span></div>
          <CardDivider />
          <CardRow>
            <CardTags>
              {tech.primaryRole && <span className="s8-tag s8-tag--accent">{tech.primaryRole}</span>}
              {tech.hasOwnEquipment && <span className="s8-tag s8-tag--teal">Own Gear</span>}
            </CardTags>
            <CardMetric>{tech.rebookRate >= 80 ? `${tech.rebookRate}% rebooked` : tech.stage8GigCount > 0 ? `${tech.stage8GigCount} gigs` : ''}</CardMetric>
          </CardRow>
        </ListingCard>
      );
    })
  );

  const emptyState = (
    <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--t3)' }}>
      <Wrench className="h-12 w-12 mb-4" style={{ color: 'var(--s8-amber)', opacity: 0.3 }} />
      <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '22px', letterSpacing: '-0.01em', lineHeight: 1.15, color: 'var(--t1)' }}>Select a Tech Pro to See Details</h2>
      <p className="mt-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px' }}>{filteredTech.length} available across the Netherlands</p>
    </div>
  );

  const detailPane = selectedId ? (
    <TechDetailPage profileId={selectedId} embedded key={selectedId} />
  ) : null;

  if (isMobile && selectedId) {
    return <TechDetailPage />;
  }

  if (!isMobile) {
    return (
      <DirectoryLayout
        title="Tech Crew"
        count={filteredTech.length}
        filters={filterPanel}
        list={cardList}
        detail={detailPane}
        emptyState={emptyState}
        sortControl={sortDropdown}
        mobileContent={null}
      />
    );
  }

  return (
    <div className="browse-page px-4 sm:px-6 py-6 sm:py-8 pb-10">
      <div className="mb-6">
        <div className="flex items-baseline justify-between">
          <h1 className="browse-title">Tech Crew</h1>
          <span className="text-[var(--s8-text-muted)]" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px' }} data-testid="available-count">{loading ? ' ' : `${filteredTech.length} available`}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4" style={{ height: '44px' }}>
        <button onClick={() => setFilterSheetOpen(true)}
          className="flex items-center gap-2 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
          style={{ fontFamily: 'var(--s8-font-condensed)', color: activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-text-muted)', border: `1px solid ${activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-border-card)'}`, background: activeFilterCount > 0 ? 'rgba(200,146,62,0.08)' : 'transparent', borderRadius: 'var(--s8-radius-md)' }}
          data-testid="filters-pill"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
        </button>
        {sortDropdown}
      </div>

      {filterSheetOpen && (
        <div className="fixed inset-0 z-50" data-testid="filter-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFilterSheetOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 px-5 pt-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] animate-slide-up" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', borderBottom: 'none', maxHeight: '75vh', overflowY: 'auto', borderTopLeftRadius: 'var(--s8-radius-xl)', borderTopRightRadius: 'var(--s8-radius-xl)' }}>
            <div className="flex items-center justify-between mb-5">
              <button onClick={clearFilters} className="text-xs transition-colors" style={{ fontFamily: 'var(--s8-font-body)', color: 'var(--s8-text-muted)' }} data-testid="filter-clear-all-mobile">Clear All</button>
              <span className="text-sm font-semibold" style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-main)', letterSpacing: '1px', textTransform: 'uppercase' }}>Filters</span>
              <button onClick={() => setFilterSheetOpen(false)} className="p-1" style={{ color: 'var(--s8-text-muted)' }}><X className="h-4 w-4" /></button>
            </div>
            {filterPanel}
            <button onClick={() => setFilterSheetOpen(false)} className="w-full py-3 text-sm font-semibold uppercase tracking-wider transition-all mt-4" style={{ fontFamily: 'var(--s8-font-condensed)', background: 'var(--s8-amber)', color: 'var(--bg)', borderRadius: 'var(--s8-radius-md)' }} data-testid="filter-apply-btn">Apply</button>
          </div>
        </div>
      )}

      <div className="browse-card-grid-wrapper">{cardList}</div>
    </div>
  );
}
